import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { store } from '../../store/store';
import { openContractGate } from '../../store/slices/contract.slice';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

class ApiService {
    private static instance: ApiService;
    private api: AxiosInstance;

    private constructor() {
        // console.log(process.env.REACT_APP_API_URL);
        
        this.api = axios.create({
            baseURL: process.env.REACT_APP_API_URL || 'http://localhost:7575/api',
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        this.setupInterceptors();
    }

    public static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService();
        }
        return ApiService.instance;
    }

    private setupInterceptors(): void {
        this.api.interceptors.request.use(
            (config) => {
                // Headers objesini garanti et
                if (!config.headers) {
                    config.headers = {} as any;
                }
                
                // Token'ı localStorage'dan al
                const token = localStorage.getItem('token');
                
                // Token varsa Authorization header'ına ekle
                if (token) {
                    // Token'ın başında "Bearer " yoksa ekle, varsa olduğu gibi kullan
                    const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
                    config.headers.Authorization = bearerToken;
                } else {
                    // Token yoksa hata fırlat (opsiyonel - debug için)
                    console.warn('API Request: No token found in localStorage');
                }
                
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        this.api.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    const currentPath = window.location.pathname;
                    const loginPaths = ['/login', '/login-2'];
                    
                    if (!loginPaths.some(path => currentPath.includes(path))) {
                        localStorage.removeItem('token');
                        window.location.href = '/login';
                    }
                }

                if (error.response?.status === 403) {
                    const respData = error.response.data;
                    const data = respData?.data ?? respData;

                    const requiredContracts: string[] =
                        data?.requiredContracts ??
                        data?.requiredContractKeys ??
                        [];

                    const contractType = data?.contractType ?? null;
                    const contractVersion =
                        typeof data?.contractVersion === "number" ? data.contractVersion : null;
                    const companyName =
                        data?.companyName ?? data?.company?.name ?? null;

                    if (requiredContracts.length > 0) {
                        store.dispatch(
                            openContractGate({
                                contractType,
                                requiredContracts,
                                contractVersion,
                                companyName,
                            })
                        );
                    } else {
                        // Eğer backend liste dönmüyorsa tek contractKey varsa onu array'e çevir.
                        const singleKey =
                            data?.contractKey ??
                            data?.contract ??
                            null;
                        if (typeof singleKey === "string") {
                            store.dispatch(
                                openContractGate({
                                    contractType,
                                    requiredContracts: [singleKey],
                                    contractVersion,
                                    companyName,
                                })
                            );
                        }
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    private extractData<T>(response: { data: { code: number; data: T } }): T {
        // console.log(response);
        
        return response.data.data;
    }

    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.get<{ code: number; data: T }>(url, config);
        return this.extractData(response);
    }

    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        
        const response = await this.api.post<{ code: number; data: T }>(url, data, config);
        
        return this.extractData(response);
    }

    public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.put<{ code: number; data: T }>(url, data, config);
        return this.extractData(response);
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.delete<{ code: number; data: T }>(url, config);
        return this.extractData(response);
    }

    public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.patch<{ code: number; data: T }>(url, data, config);
        return this.extractData(response);
    }

    // FormData desteği için özel metodlar
    public async postFormData<T>(url: string, formData: FormData): Promise<T> {
        const config: AxiosRequestConfig = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        
        const response = await this.api.post<{ code: number; data: T }>(url, formData, config);
        return this.extractData(response);
    }

    public async putFormData<T>(url: string, formData: FormData): Promise<T> {
        const config: AxiosRequestConfig = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };
        
        const response = await this.api.put<{ code: number; data: T }>(url, formData, config);
        return this.extractData(response);
    }
}

export default ApiService.getInstance(); 